/** An imported figure's receipt as a chain (gate 22).
 *
 * `receiptLinks.ts` decides the links of a *calculated* figure from the server's six-link evidence
 * assessment. An imported figure is **not assessed that way** - it is rule 3's second species
 * (ADR-0032): asserted by a person from a named source, never calculated here - so it has its own
 * short chain, drawn by the same `ReceiptChain` so an auditor reads both kinds the same way:
 *
 * 1. **Source document** - the file the assertion was made from, if one was cited;
 * 2. **Assertion** - who asserted it and when (a person's id, never a name: rule 4);
 * 3. **Calculation** - *not applicable*, and says why in words, which is a different statement
 *    from a calculated figure's missing link;
 * 4. **Ledger entry** - the `figure.imported` entry that recorded the assertion.
 *
 * The same three standings are used, and none collapses into another: a figure that cites no
 * document is **not applicable** at the source (the assertion is the evidence, as a stated basis
 * is for a calculated figure), whereas a cited document that cannot be found is a **break**.
 * Pure, so each standing is asserted without rendering a screen.
 */

import type { components } from "../api/schema";
import type { ChainNode } from "./receiptLinks";

export type ImportedChainReceipt = components["schemas"]["ImportedReceiptChainOut"];

export type ImportedNodeKey = "source" | "assertion" | "calculation" | "ledger";

export function buildImportedChain(receipt: ImportedChainReceipt): ChainNode<ImportedNodeKey>[] {
  const nodes: ChainNode<ImportedNodeKey>[] = [];

  // --- 1. The source document ------------------------------------------------------------------
  if (receipt.source_document) {
    nodes.push({ key: "source", label: "Source document", standing: "present", reason: null, links: [] });
  } else if (receipt.source_document_id) {
    // An id that resolves to nothing: the figure names a document and does not reach one. Said as
    // a break, never as a blank row, so nobody assumes the file exists somewhere.
    nodes.push({
      key: "source",
      label: "Source document",
      standing: "missing",
      reason: "The document this assertion cites could not be found.",
      links: [],
    });
  } else {
    nodes.push({
      key: "source",
      label: "Source document",
      standing: "not_applicable",
      reason:
        "No document was cited: this figure rests on the assertion below, from the source it names.",
      links: [],
    });
  }

  // --- 2. The assertion: a person, a date --------------------------------------------------------
  nodes.push({ key: "assertion", label: "Assertion", standing: "present", reason: null, links: [] });

  // --- 3. The calculation: there was none -------------------------------------------------------
  nodes.push({
    key: "calculation",
    label: "Calculation",
    standing: "not_applicable",
    reason:
      "Not calculated by EmitIQ: nothing was calculated, so there is nothing to price or re-run. The figure is held so it can be stated as a comparative and reconciled, never added to an inventory.",
    links: [],
  });

  // --- 4. The ledger entry -----------------------------------------------------------------------
  const recorded = receipt.ledger.length > 0;
  nodes.push({
    key: "ledger",
    label: "Ledger entry",
    standing: recorded ? "present" : "missing",
    reason: recorded ? null : "No entry - this assertion is not on the hash chain.",
    links: [],
  });

  return nodes;
}
